import { useState } from 'react';
import { api, useAuth } from '@halo/web-lib';
import { Lock, X } from 'lucide-react';

export function PasswordSetupBanner() {
  const { user, updateUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user?.needsPassword || dismissed) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<null>('/api/v1/auth/setup-account', {
        password,
        displayName: displayName || undefined,
      });
      if (!res.success) throw new Error(res.error ?? 'Failed');
      updateUser({ needsPassword: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-b border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
      <div className="flex items-center gap-2 px-4 py-2.5 text-blue-800 dark:text-blue-200 text-[13px]">
        <Lock className="w-4 h-4 shrink-0" />
        <span className="flex-1">
          Set a password so you can sign in later.{' '}
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="font-medium underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
          >
            {expanded ? 'Hide' : 'Set up now'}
          </button>
        </span>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="shrink-0 p-0.5 rounded hover:bg-blue-200/50 dark:hover:bg-blue-800/50 transition-colors"
          title="Dismiss for this session"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <form onSubmit={handleSubmit} className="px-4 pb-3 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-[11px] font-medium text-blue-700 dark:text-blue-300 mb-0.5">Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Optional"
              className="px-2.5 py-1.5 border border-blue-200 dark:border-blue-700 rounded-md text-sm bg-white dark:bg-grey-700 text-charcoalGray-800 dark:text-white outline-none focus:border-primary-500 w-40"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-blue-700 dark:text-blue-300 mb-0.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 8 characters"
              required
              minLength={8}
              autoComplete="new-password"
              className="px-2.5 py-1.5 border border-blue-200 dark:border-blue-700 rounded-md text-sm bg-white dark:bg-grey-700 text-charcoalGray-800 dark:text-white outline-none focus:border-primary-500 w-48"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
        </form>
      )}
    </div>
  );
}
